import type { FunctionComponent, ReactNode } from 'react';

import { useContext } from 'react';
import { Anchor, Box, Card, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { useAuth } from 'hooks/useAuth';
import { GENERATED_MENU_SOURCE, MENU_UPLOAD_FEEDBACK_URL } from 'constants/menuManagement';
import MenuInfoModal from 'components/Modals/MenuInfoModal/MenuInfoModal';

import { AiIconSm, CameraIconSm, CopyIconMd, NoteIconSm, StarIconSm } from '@chownow/pantry/icons';
import MenuEdit from './MenuEdit';

type MenuItemsNoticeCardsProps = {
  onNavigateToPhotos: () => void;
};

const ICON_BG = {
  ai: 'var(--surface-success)',
  camera: 'var(--surface-paper-1)',
  copy: 'var(--surface-tertiary)',
  note: 'var(--surface-paper-3)',
  star: 'var(--surface-inverse-primary)',
};

const iconContainerStyle = {
  borderRadius: 'var(--mantine-radius-xs2)',
  width: 'var(--dimension-md)',
  height: 'var(--dimension-md)',
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
};

export const MenuItemsNoticeCards: FunctionComponent<MenuItemsNoticeCardsProps> = ({
  onNavigateToPhotos,
}) => {
  const { menu, isCateringMenu } = useContext(MenuEdit.Context);
  const { menuUploadWizard } = useFlags();
  const { merchants, companies } = useAuth();
  const [infoModalOpened, infoModal] = useDisclosure(false);

  const isMasterMenu = (menu?.associatedMerchants?.length ?? 0) > 0;
  const isClonedMenu = !!menu?.clonedMenu;
  const isGeneratedMenu = menu?.menuSource === GENERATED_MENU_SOURCE;
  const syncedCount = menu?.associatedMerchants?.length ?? 0;

  let menuTypeIcon = <NoteIconSm />;
  let menuTypeIconBg = ICON_BG.note;
  let menuTypeIconColor = 'var(--content-accent-primary)';
  let menuTypeTitle: string;
  let menuTypeDescription: ReactNode;

  if (isMasterMenu) {
    menuTypeIcon = <StarIconSm />;
    menuTypeIconBg = ICON_BG.star;
    menuTypeIconColor = 'var(--content-inverse-primary)';
    menuTypeTitle = isCateringMenu ? 'This is a master catering menu' : 'This is a master menu';
    menuTypeDescription = (
      <>
        Edits here affect{' '}
        <Anchor component="button" size="sm" underline="always" onClick={infoModal.open}>
          {syncedCount} {syncedCount === 1 ? 'synced menu' : 'synced menus'}
        </Anchor>
        .
      </>
    );
  } else if (isClonedMenu) {
    menuTypeIcon = <CopyIconMd />;
    menuTypeIconBg = ICON_BG.copy;
    menuTypeIconColor = 'var(--content-primary)';
    menuTypeTitle = isCateringMenu ? 'This is a cloned catering menu' : 'This is a cloned menu';
    menuTypeDescription = 'Some editing features are restricted.';
  } else {
    menuTypeTitle = isCateringMenu ? 'This is a catering menu' : 'This is a standard menu';
    menuTypeDescription = 'Not synced to other menus.';
  }

  return (
    <>
      <SimpleGrid cols={{ base: 1, md: isGeneratedMenu && menuUploadWizard ? 3 : 2 }} spacing="sm">
        <Card px="md" py="sm">
          <Group gap="sm" wrap="nowrap" align="center">
            <Box bg={menuTypeIconBg} style={{ ...iconContainerStyle, color: menuTypeIconColor }}>
              {menuTypeIcon}
            </Box>
            <Stack gap={0}>
              <Text variant="body-sm-bold">{menuTypeTitle}</Text>
              <Text>{menuTypeDescription}</Text>
            </Stack>
          </Group>
        </Card>
        {isGeneratedMenu && menuUploadWizard && (
          <Card px="md" py="sm">
            <Group gap="sm" wrap="nowrap" align="center">
              <Box
                bg={ICON_BG.ai}
                style={{ ...iconContainerStyle, color: 'var(--content-accent-primary)' }}
              >
                <AiIconSm />
              </Box>
              <Stack gap={0}>
                <Text variant="body-sm-bold">This menu was auto generated</Text>
                <Text>
                  Have thoughts?{' '}
                  <Anchor
                    href={MENU_UPLOAD_FEEDBACK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    underline="always"
                  >
                    Share feedback.
                  </Anchor>
                </Text>
              </Stack>
            </Group>
          </Card>
        )}
        <Card px="md" py="sm">
          <Group gap="sm" wrap="nowrap" align="center">
            <Box
              bg={ICON_BG.camera}
              style={{ ...iconContainerStyle, color: 'var(--mantine-color-neutral-8)' }}
            >
              <CameraIconSm />
            </Box>
            <Stack gap={0}>
              <Text variant="body-sm-bold">Photos drive more orders</Text>
              <Text>
                Boost sales by{' '}
                <Anchor
                  component="button"
                  size="sm"
                  underline="always"
                  onClick={onNavigateToPhotos}
                >
                  improving photos
                </Anchor>
                .
              </Text>
            </Stack>
          </Group>
        </Card>
      </SimpleGrid>
      {isMasterMenu && (
        <MenuInfoModal
          opened={infoModalOpened}
          close={infoModal.close}
          menu={menu}
          merchants={merchants}
          companies={companies}
        />
      )}
    </>
  );
};
